import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'ui/atoms/translate';
import WithDataRecord from 'hoc/WithDataRecord';
import Button from 'src/ui/atoms/button';
import Date from 'src/ui/atoms/date';
import {
  ProductsForPublicationApi,
  ProductsForPublicationListOrderingEnum,
  ProductsForPublicationValuesRetrieveFieldEnum,
} from 'api';
import { AnonymousProduct } from 'src/api';
import * as Styled from './styled';
import { Spacer } from 'ui/atoms/spacer';
import CopyButton from 'src/ui/molecules/copy-button';
import useApiCall from 'src/hooks/use-api-call';
import { TruncatedTooltip } from 'src/ui/atoms/truncated-tooltip';

export interface PublicationsTableProps {
  onOpenHistory: (id: string, isin?: string | null) => void;
  defaultLimit: number;
  forceDataLoad?: boolean;
  title?: ReactNode;
  loading?: boolean;
  singleLabelMultipleOptions?: { value: any[]; label: string; name: string }[];
  singleLabelMultipleOptionsFilterName?: string;
}

const PublicationsTable: FunctionComponent<PublicationsTableProps> = WithDataRecord<
  ProductsForPublicationApi,
  AnonymousProduct
>(ProductsForPublicationApi)(
  ({ data, paginationProps, title, loading, onOpenHistory, ordering, onOrderBy }) => {
    const { makeAuthenticatedApi } = useApiCall();
    const productsForPublicationApi: ProductsForPublicationApi = useMemo(
      () => makeAuthenticatedApi(ProductsForPublicationApi),
      [makeAuthenticatedApi],
    );

    return (
      <>
        <DataRecordTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          data={data?.map((publication) => ({ ...publication, disabled: publication.publicationValid === false }))}
          title={title}
          loading={loading}
          paginationProps={paginationProps}
          cells={[
            {
              title: <Translate name={`publications.table.isin`} />,
              render: ({ isin }) => isin,
              type: CellTypeEnum.text,
              filterName: 'isin',
              searchFieldName: ProductsForPublicationValuesRetrieveFieldEnum.ISIN,
              getFilterData: (params: any) => productsForPublicationApi.productsForPublicationValuesRetrieve(params),
            },
            {
              title: <Translate name={`publications.table.code`} />,
              render: ({ assetCode }) => assetCode,
              type: CellTypeEnum.text,
              filterName: 'assetCode',
              searchFieldName: ProductsForPublicationValuesRetrieveFieldEnum.ASSET_CODE,
              getFilterData: (params: any) => productsForPublicationApi.productsForPublicationValuesRetrieve(params),
            },
            {
              title: <Translate name={`publications.table.product`} />,
              render: ({ name }) => name,
              filterName: 'name',
              searchFieldName: ProductsForPublicationValuesRetrieveFieldEnum.NAME,
              getFilterData: (params: any) => productsForPublicationApi.productsForPublicationValuesRetrieve(params),
            },
            {
              title: <Translate name={`publications.table.issuer`} />,
              render: ({ issuerCompanyName }) => issuerCompanyName,
              type: CellTypeEnum.text,
              filterName: 'issuerCompanyName',
              searchFieldName: ProductsForPublicationValuesRetrieveFieldEnum.ISSUER_COMPANY_NAME,
              getFilterData: (params: any) => productsForPublicationApi.productsForPublicationValuesRetrieve(params),
            },
            {
              title: <Translate name={`publications.table.network`} />,
              render: ({ network }) => network,
              type: CellTypeEnum.text,
              filterName: 'network',
              searchFieldName: ProductsForPublicationValuesRetrieveFieldEnum.NETWORK,
              getFilterData: (params: any) => productsForPublicationApi.productsForPublicationValuesRetrieve(params),
            },
            {
              title: <Translate name={`publications.table.smartContractAddress`} />,
              render: ({ smartContractAddress }) => smartContractAddress,
              copyValue: ({ smartContractAddress }) =>
                smartContractAddress && <CopyButton valueToCopy={smartContractAddress} />,
              type: CellTypeEnum.text,
              filterName: 'smartContractAddress',
              searchFieldName: ProductsForPublicationValuesRetrieveFieldEnum.SMART_CONTRACT_ADDRESS,
              getFilterData: (params: any) => productsForPublicationApi.productsForPublicationValuesRetrieve(params),
            },
            {
              title: <Translate name={`publications.table.version`} />,
              render: ({ lastIssuanceDocument }) => {
                if (!lastIssuanceDocument) return '-';

                return (
                  <Styled.TableCellContent>
                    <Styled.InlineContent>
                      {lastIssuanceDocument.version}
                      <Spacer x={2} />
                      <Styled.DownloadIcon
                        href={lastIssuanceDocument.document.file}
                        target="_blank"
                        icon="download"
                        download={true}
                        iconColor="primary"
                      />
                    </Styled.InlineContent>
                  </Styled.TableCellContent>
                );
              },
              type: CellTypeEnum.text,
              filterName: 'latestPublicationVersion',
              searchFieldName: ProductsForPublicationValuesRetrieveFieldEnum.LATEST_PUBLICATION_VERSION,
              getFilterData: (params: any) => productsForPublicationApi.productsForPublicationValuesRetrieve(params),
            },
            {
              title: <Translate name={`publications.table.releaseDate`} />,
              render: ({ lastIssuanceDocument }) =>
                lastIssuanceDocument?.publicationDate && <Date>{lastIssuanceDocument?.publicationDate}</Date>,
              type: CellTypeEnum.date,
              filterName: 'publicationDate',
            },
            {
              title: <Translate name={`publications.table.history`} />,
              width: 100,
              render: ({ id, publicationValid, lastIssuanceDocument, isin }) => {
                if (!lastIssuanceDocument) return '-';
                if (publicationValid) {
                  return (
                    <Button variant="link" color="primary" onClick={() => onOpenHistory(id, isin)} size="small">
                      <Translate name={`publications.table.showHistory`} />
                    </Button>
                  );
                }
                return (
                  <Styled.TableCellContentCentered>
                    <Styled.TableCellContentButton
                      variant="link"
                      color="primary"
                      onClick={() => onOpenHistory(id, isin)}
                      size="small"
                    >
                      <Translate name={`publications.table.showHistory`} />
                    </Styled.TableCellContentButton>
                    <TruncatedTooltip>
                      <Translate name={`publications.table.relevant`} />
                    </TruncatedTooltip>
                  </Styled.TableCellContentCentered>
                );
              },
              alignContent: 'center',
            },
          ]}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.productsForPublicationList({
      offset,
      ordering: ordering as ProductsForPublicationListOrderingEnum,
      limit,
      ...props,
    });
  },
);

export default PublicationsTable;
